import { useState, useMemo } from "react";
import { Box, CssBaseline } from "@mui/material";
import Sidebar from "./Sidebar";
import type { TandemView } from "./Sidebar";
import TopBar from "./TopBar";
import type { FilterOption } from "./TopBar";
import Overview from "./Overview";
import ConsultantsScreen from "./ConsultantsScreen";
import ScheduleScreen from "./ScheduleScreen";
import ConsultantDetail from "./ConsultantDetail";
import ReportsScreen from "./ReportsScreen";
import PoolScreen from "./PoolScreen";
import SettingsScreen from "./SettingsScreen";
import PointsSettings from "./PointsSettings";
import EngineSettingsScreen from "./EngineSettingsScreen";
import { T } from "./tokens";
import { PointsConfigProvider } from "./pointsConfig";
import { useWeeklyVisitSummary, useActiveConsultants, toISODate, addDays, addMonths, mondayOf, weekdayDates, weekRangeLabel, monthWeekdays } from "./data";
import type { VisitConsultantT } from "./data";

const TITLES: Record<TandemView, [string, string]> = {
    overview: ["Overview", "Your visits, targets and the current booking window"],
    schedule: ["Consultant schedule", "Booked visits per consultant — click a consultant for detail"],
    pool: ["Visit pool", "Work that is owed but not yet booked, over a rolling due-date horizon"],
    reports: ["Performance report", "Visits, days and utilisation per consultant — see the notes on how points are derived"],
    consultants: ["Consultants", "Utilisation and visit activity per consultant — click a card for detail"],
    settings: ["Settings", "Console configuration"],
    points: ["Points model", "How a visit's points are worked out, and the values behind them"],
    engine: ["Engine settings", "Phase 2 — auto-booking policy, not connected yet"],
};

const TandemConsole = () => {
    // Console pages that can be hidden from the sidebar (Settings → Console pages).
    const DEFAULT_HIDDEN: TandemView[] = ["engine"];
    const [hiddenPages, setHiddenPages] = useState<Set<TandemView>>(() => {
        try {
            const raw = localStorage.getItem("tandem.hiddenPages.v3");
            if (raw) return new Set(JSON.parse(raw) as TandemView[]);
        } catch { /* fall through to default */ }
        return new Set(DEFAULT_HIDDEN);
    });
    const togglePage = (key: string) =>
        setHiddenPages((prev) => {
            const next = new Set(prev);
            if (next.has(key as TandemView)) next.delete(key as TandemView);
            else next.add(key as TandemView);
            try { localStorage.setItem("tandem.hiddenPages.v3", JSON.stringify([...next])); } catch { /* non-fatal */ }
            return next;
        });

    const [view, setView] = useState<TandemView>("overview");
    const [team, setTeam] = useState("AllTeams");
    const [consultant, setConsultant] = useState("AllConsultants");
    const [periodMode, setPeriodMode] = useState<"week" | "month">("week");
    const [weekOffset, setWeekOffset] = useState(0);
    const [monthOffset, setMonthOffset] = useState(0);
    const [detail, setDetail] = useState<{ consultant: VisitConsultantT; teamName: string } | null>(null);

    const today = new Date();
    const weekDate = addDays(today, weekOffset * 7);
    const monday = mondayOf(weekDate);
    const weekDates = weekdayDates(monday);
    const monthBase = addMonths(today, monthOffset);

    // The dates driving Schedule / Reports: 5 weekdays, or every weekday of the month.
    const dates = periodMode === "week" ? weekDates : monthWeekdays(monthBase);
    const periodLabel =
        periodMode === "week"
            ? weekRangeLabel(monday)
            : monthBase.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    const stepPrev = () => (periodMode === "week" ? setWeekOffset((w) => w - 1) : setMonthOffset((m) => m - 1));
    const stepNext = () => (periodMode === "week" ? setWeekOffset((w) => w + 1) : setMonthOffset((m) => m + 1));
    // The weekly-summary endpoint covers ~4 weeks around the passed date; mid-month
    // gives the best consultant roster coverage for month view.
    const summaryDate = periodMode === "week" ? weekDate : new Date(monthBase.getFullYear(), monthBase.getMonth(), 15);

    // Month heatmap cell → jump into that week's detailed view.
    const jumpToDate = (d: Date) => {
        const diffDays = Math.round((mondayOf(d).getTime() - mondayOf(today).getTime()) / 86400000);
        setWeekOffset(Math.round(diffDays / 7));
        setPeriodMode("week");
    };

    // Overview needs the summary too now — one query, the same cached key the
    // other screens use, not the 85 per-consultant ones.
    const needsWeekly =
        view === "overview" || view === "schedule" || view === "consultants" || view === "reports" || !!detail;
    const weekly = useWeeklyVisitSummary(toISODate(summaryDate), needsWeekly);

    // Filter options come from the ROSTER, not from whoever has a visit this week,
    // or a team with a quiet week vanishes from its own filter.
    const roster = useActiveConsultants();

    const teamOptions: FilterOption[] = useMemo(() => {
        const names = new Set(roster.active.map((c) => c.team || "Unknown"));
        return [
            { value: "AllTeams", label: "All teams" },
            ...[...names].sort().map((n) => ({ value: n, label: n })),
        ];
    }, [roster.active]);

    // Narrowed by the team already chosen, so the list is the people you could
    // actually be looking at.
    const consultantOptions: FilterOption[] = useMemo(() => {
        const people = roster.active
            .filter((c) => team === "AllTeams" || (c.team || "Unknown") === team)
            .filter((c) => !!c.email)
            .sort((a, b) => a.name.localeCompare(b.name));
        return [
            { value: "AllConsultants", label: `All consultants (${people.length})` },
            ...people.map((c) => ({ value: c.email, label: c.name })),
        ];
    }, [roster.active, team]);

    // A figure on Overview can carry its own filter into the screen it opens, so
    // "12 free days" lands on the schedule already showing only those rows.
    const [scheduleGapsOnly, setScheduleGapsOnly] = useState(false);
    const [consultantsNothingBooked, setConsultantsNothingBooked] = useState(false);

    const navigate = (v: TandemView) => {
        setDetail(null);
        setScheduleGapsOnly(false);
        setConsultantsNothingBooked(false);
        setView(v);
    };
    const openConsultant = (teamName: string, consultant: VisitConsultantT) => {
        setDetail({ consultant, teamName });
    };

    const [title, sub] = detail
        ? [detail.consultant.name, `${detail.teamName || "No team"} · consultant insights`]
        : TITLES[view];

    const renderScreen = () => {
        if (detail)
            return (
                <ConsultantDetail
                    consultant={detail.consultant}
                    teamName={detail.teamName}
                    weekMonday={monday}
                    dates={dates}
                    mode={periodMode}
                    periodLabel={periodLabel}
                    onJumpToDate={jumpToDate}
                />
            );
        switch (view) {
            case "overview":
                return (
                    <Overview
                        data={weekly.data}
                        isLoading={weekly.isLoading}
                        isError={weekly.isError}
                        team={team}
                        dates={dates}
                        onGoSchedule={(gaps) => {
                            setDetail(null);
                            setScheduleGapsOnly(!!gaps);
                            setView("schedule");
                        }}
                        onGoConsultants={(nothingBooked) => {
                            setDetail(null);
                            setConsultantsNothingBooked(!!nothingBooked);
                            setView("consultants");
                        }}
                        onGoPool={() => navigate("pool")}
                        onGoReports={() => navigate("reports")}
                    />
                );
            case "consultants":
                return (
                    <ConsultantsScreen
                        team={team}
                        consultant={consultant}
                        data={weekly.data}
                        isLoading={weekly.isLoading}
                        isError={weekly.isError}
                        dates={dates}
                        initialNothingBookedOnly={consultantsNothingBooked}
                        onOpen={openConsultant}
                    />
                );
            case "schedule":
                return (
                    <ScheduleScreen
                        team={team}
                        consultant={consultant}
                        data={weekly.data}
                        isLoading={weekly.isLoading}
                        isError={weekly.isError}
                        dates={dates}
                        mode={periodMode}
                        initialGapsOnly={scheduleGapsOnly}
                        onJumpToDate={jumpToDate}
                        onOpen={openConsultant}
                    />
                );
            case "reports":
                return (
                    <ReportsScreen
                        team={team}
                        consultant={consultant}
                        data={weekly.data}
                        isLoading={weekly.isLoading}
                        isError={weekly.isError}
                        dates={dates}
                    />
                );
            case "engine":
                return <EngineSettingsScreen />;
            case "pool":
                return <PoolScreen team={team} />;
            case "points":
                return <PointsSettings />;
            case "settings":
                return <SettingsScreen hiddenPages={hiddenPages} onTogglePage={togglePage} />;
            default:
                return null;
        }
    };

    return (
        <PointsConfigProvider>
        <Box sx={{ display: "flex", height: "100vh", width: "100%", fontFamily: T.font, color: T.ink2, background: T.bg, overflow: "hidden" }}>
            <CssBaseline />
            <Sidebar active={view} onNavigate={navigate} hiddenPages={hiddenPages} />

            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                <TopBar
                    title={title}
                    sub={sub}
                    showBack={!!detail}
                    onBack={() => setDetail(null)}
                    team={team}
                    teamOptions={teamOptions}
                    onTeam={setTeam}
                    consultant={consultant}
                    consultantOptions={consultantOptions}
                    onConsultant={setConsultant}
                    weekLabel={periodLabel}
                    onPrevWeek={stepPrev}
                    onNextWeek={stepNext}
                    periodMode={periodMode}
                    onPeriodMode={setPeriodMode}
                />

                <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>{renderScreen()}</Box>
            </Box>
        </Box>
        </PointsConfigProvider>
    );
};

export default TandemConsole;
